import projectKeys from '@/api/queries/project/keyFactory';
import useGetProjectIntegrations from '@/api/queries/project/useGetProjectIntegrations';
import { useMyAccountHasFeature } from '@/api/queries/me/useGetMyAccountFeatures';
import { useAccount } from '@/api/rest/users/useAccount';
import usePreferredNumberFormat from '@/hooks/usePreferredNumberFormat';
import { AccountRegions } from '@payaca/types/accountTypes';
import { useHashFragment } from '@/utils/customHooks';
import {
  Button,
  Card,
  Divider,
  Modal,
  useToastContext,
} from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { useQueryClient } from '@tanstack/react-query';
import { FC, PropsWithChildren } from 'react';
import CollapsibleCard from '../CollapsibleCard';
import { GridElement } from '../GridElement';
import { PropertyInfoForm } from '../propertyInfo/PropertyInfoForm';
import {
  hasFabricDetails,
  PropertyInfoSeed,
} from '../propertyInfo/propertyInfoFormHelpers';
import {
  BUILT_FORM_OPTIONS,
  CONSTRUCTION_AGE_BAND_OPTIONS,
  getWallInsulationOptions,
  HEATING_SYSTEM_OPTIONS,
  OCCUPANCY_ARCHETYPE_OPTIONS,
  PROPERTY_TYPE_OPTIONS,
  WALL_CONSTRUCTION_OPTIONS,
  WINDOW_GLAZING_OPTIONS,
} from '../propertyInfo/propertyInfoOptions';

type PropertyDetailsCardProps = {
  projectId: string;
  addressId: string;
  propertyInfo?: PropertyInfoSeed;
};

const DASH = '-';

const optionLabel = (
  options: { label: string; value: string }[],
  value?: string | null
): string => options.find((o) => o.value === value)?.label ?? DASH;

const Section: FC<PropsWithChildren<{ title: string; divider?: boolean }>> = (
  props
) => {
  const { title, divider = true, children } = props;
  return (
    <>
      {divider && <Divider />}
      <h4>{title}</h4>
      {/* oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Auto-fill grid, so the column count follows the card width */}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-4 mt-4">
        {children}
      </div>
    </>
  );
};

/**
 * Property tab card letting a provider manually enter property details, which
 * generate a heat loss estimate through the address regeneration pipeline.
 */
const PropertyDetailsCard: FC<PropertyDetailsCardProps> = (props) => {
  const { projectId, addressId, propertyInfo } = props;
  const [isOpen, toggle] = useHashFragment('#property-details');
  const { pushSuccess } = useToastContext();
  const queryClient = useQueryClient();
  const numberFormat = usePreferredNumberFormat();
  const account = useAccount();
  const showOccupancy = account.region === AccountRegions.UK;
  // The electricity facts feed the solar estimate only.
  const showElectricity = useMyAccountHasFeature('hasSolarDesignTool');

  // Only fetch integrations while the edit modal is open, so we don't fire the
  // query on every Property tab view.
  const { integrations } = useGetProjectIntegrations(
    isOpen ? projectId : undefined
  );
  const isLinkedToSpruce =
    integrations?.some((integration) => integration.service === 'SPRUCE') ??
    false;

  const showFabric = hasFabricDetails(propertyInfo);

  // Heat loss regeneration runs in an async background job after saving, so a
  // single immediate refetch would return the previous estimate. Poll the query
  // briefly until its value changes (bounded), so the card updates on its own.
  const pollForRegeneratedHeatLossEstimate = async () => {
    const queryKey = projectKeys.heatLossEstimate(projectId);
    const before = JSON.stringify(queryClient.getQueryData(queryKey) ?? null);
    for (let attempt = 0; attempt < 5; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      await queryClient.invalidateQueries({ queryKey });
      const after = JSON.stringify(queryClient.getQueryData(queryKey) ?? null);
      if (after !== before) return;
    }
  };

  return (
    <>
      {propertyInfo ? (
        <CollapsibleCard
          title="Property details"
          summary={optionLabel(
            PROPERTY_TYPE_OPTIONS,
            propertyInfo.propertyType
          )}
          actions={
            <Button size="sm" variant="secondary" onClick={toggle}>
              Edit property details
            </Button>
          }
        >
          <Section title="Building" divider={false}>
            <GridElement title="Property type">
              {optionLabel(PROPERTY_TYPE_OPTIONS, propertyInfo.propertyType)}
            </GridElement>
            <GridElement title="Built form">
              {optionLabel(BUILT_FORM_OPTIONS, propertyInfo.builtForm)}
            </GridElement>
            <GridElement title="Construction age band">
              {optionLabel(
                CONSTRUCTION_AGE_BAND_OPTIONS,
                propertyInfo.constructionAgeBand
              )}
            </GridElement>
            <GridElement title="Floor area">
              {propertyInfo.floorAreaM2 != null
                ? `${numberFormat.format(propertyInfo.floorAreaM2)} m²`
                : DASH}
            </GridElement>
            <GridElement title="Number of bedrooms">
              {propertyInfo.numBedrooms ?? DASH}
            </GridElement>
            <GridElement title="Number of storeys">
              {propertyInfo.numStoreys ?? DASH}
            </GridElement>
          </Section>

          <Section title="Heating">
            <GridElement title="Heating system">
              {optionLabel(HEATING_SYSTEM_OPTIONS, propertyInfo.heatingSystem)}
            </GridElement>
            <GridElement title="Annual heating energy use">
              {propertyInfo.heatingEnergyUseKwh != null
                ? `${numberFormat.format(propertyInfo.heatingEnergyUseKwh)} kWh`
                : DASH}
            </GridElement>
          </Section>

          {showElectricity && (
            <Section title="Electricity">
              {showOccupancy && (
                <GridElement title="Weekday occupancy">
                  {optionLabel(
                    OCCUPANCY_ARCHETYPE_OPTIONS,
                    propertyInfo.electricityOccupancyArchetype
                  )}
                </GridElement>
              )}
              <GridElement title="Annual electricity consumption">
                {propertyInfo.annualElectricityConsumptionKwh != null
                  ? `${numberFormat.format(propertyInfo.annualElectricityConsumptionKwh)} kWh`
                  : DASH}
              </GridElement>
            </Section>
          )}

          {showFabric && (
            <Section title="Fabric & insulation">
              <GridElement title="Wall construction">
                {optionLabel(
                  WALL_CONSTRUCTION_OPTIONS,
                  propertyInfo.wallConstruction
                )}
              </GridElement>
              <GridElement title="Wall insulation">
                {optionLabel(
                  getWallInsulationOptions(
                    propertyInfo.wallConstruction ?? undefined
                  ),
                  propertyInfo.wallInsulation
                )}
              </GridElement>
              <GridElement title="Window glazing type">
                {optionLabel(
                  WINDOW_GLAZING_OPTIONS,
                  propertyInfo.windowGlazingType
                )}
              </GridElement>
              <GridElement title="Loft insulation">
                {propertyInfo.loftInsulationMm != null
                  ? `${numberFormat.format(propertyInfo.loftInsulationMm)} mm`
                  : DASH}
              </GridElement>
            </Section>
          )}
        </CollapsibleCard>
      ) : (
        <Card>
          <Card.Body>
            <div className="flex items-start justify-between gap-4">
              <h3>Property details</h3>
              <Button size="sm" variant="secondary" onClick={toggle}>
                <UntitledIcon name="plus.3" className="h-5 w-5" />
                Enter property details
              </Button>
            </div>
            <p>
              Enter property details to generate a heat loss estimate for a heat
              pump, unlocking product matching and bill savings.
            </p>
          </Card.Body>
        </Card>
      )}

      <Modal
        title="Property details"
        isOpen={isOpen}
        onClose={toggle}
        size="md"
      >
        {isOpen && (
          <PropertyInfoForm
            addressId={addressId}
            propertyInfo={propertyInfo}
            submitLabel="Save property details"
            variant="heatLoss"
            notice={
              isLinkedToSpruce
                ? "This project is linked to Spruce. Changes here won't update the linked Spruce job."
                : undefined
            }
            onSaved={() => {
              void queryClient.invalidateQueries({
                queryKey: projectKeys.overview(projectId),
              });
              pushSuccess('Property details saved');
              toggle();
              // Fire-and-forget so the form's saved state isn't held open for
              // the whole poll.
              void pollForRegeneratedHeatLossEstimate();
            }}
          />
        )}
      </Modal>
    </>
  );
};

export default PropertyDetailsCard;
